import React, { memo, useState, useEffect, useMemo } from 'react'
import { MotifChipGroup, MotifChip, MotifToast, MotifModal, MotifModalHeader, MotifModalBody, MotifModalFooter, MotifButton, MotifIcon, MotifIconButton } from '@ey-xd/motif-react'
import { MotifActionIcDelete24px } from '@ey-xd/motif-icon'
import './ExtractedTextItem.scss'
import { useSelector, shallowEqual } from 'react-redux'
import {
  useLazyGetExtractedTextQuery,
  useLazyDeleteAnnotationQuery,
  useLazyGetAnnotationListQuery,
  useConfirmNoSystemAccountMutation,
} from '@/services/application/annotateReducer'
import { UserDetail } from './UserDetails'
import { UserGroup } from './UserGroup'
import { UserPermission } from './UserPermission'
import { OtherDetails } from './OtherDetails'
import { DateTime } from './DateTime'
import Table from '@/components/Table/table'
import { ConstAnnotationTypeId } from '../Constant'
import {
  addAnnotationList,
  removeAnnotationByJobId,
  setExtractedText as setAnnotationExtractedText,
  storeChunksForAnnotation,
  setSupplementRequired
} from '../AnnotateSlice'
import { useDispatch } from 'react-redux'
import { getExtractedData } from './helper'
import { IconoirInfoCircle } from '@ey-xd/motif-icon'
interface ExtractedTextItemProps {
  evidenceId: string
  onSelectedAnnotation: (annotation: any) => void
  currentPageNumber?: number
  onDeleteAnnotation: (annotationId: string) => void
  onSystemAccountAcknowledged?: () => void
}
const ExtractedTextItem = memo(
  ({ evidenceId, onSelectedAnnotation, currentPageNumber, onDeleteAnnotation, onSystemAccountAcknowledged }: ExtractedTextItemProps) => {
    const EmptyTitleComplement: React.FC = () => null
    const [selectedAnnotate, setSelectedAnnotate] = useState('')
    const [toastMessage, setToastMessage] = useState<{
      isShow: boolean
      message: string
      type: 'warning' | 'success' | 'error' | undefined
    }>({
      isShow: false,
      message: '',
      type: undefined,
    })
    const [shouldRunEffect, setShouldRunEffect] = useState(true)
    const [extractedText, setExtractedText] = useState<any[]>([])
    const [selectedAnnotateType, setSelectedAnnotateType] = useState('')
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [deleteRecordModal, setDeleteRecordModal] = useState<{
      show: boolean
      chunkIndex: number
      recordIndex: number
      annotationTypeId: string
      record: any
    }>({
      show: false,
      chunkIndex: -1,
      recordIndex: -1,
      annotationTypeId: '',
      record: null,
    })
    const [showPlaceholderLimitError, setShowPlaceholderLimitError] = useState(false)
    const [annotationCounts, setAnnotationCounts] = useState<Record<string, number>>({})
    const [deleteAnnotationModal, setDeleteAnnotationModal] = useState<{
      show: boolean
      item: any | null
    }>({ show: false, item: null })
    const [systemAccountDetected, setSystemAccountDetected] = useState<any[]>([])
    const annotationCountsRef = React.useRef<Record<string, number>>({})
    const fetchedIdsRef = React.useRef<Set<string>>(new Set())
    const [getExtractedText] = useLazyGetExtractedTextQuery()
    const [deleteAnnotation] = useLazyDeleteAnnotationQuery()
    const [getAnnotationList] = useLazyGetAnnotationListQuery()
    const [confirmNoSystemAccount] = useConfirmNoSystemAccountMutation()
    const dispatch = useDispatch()
    const annotationListStatus = useSelector(
      (state: any) => state.Annotate.filter((item: any) => item.evidenceId === evidenceId && item.pageNumber === currentPageNumber),
      shallowEqual,
    )
    const isSupplymentRequired = useSelector(
      (state: any) => state.Annotate
        .filter((item: any) => item.evidenceId === evidenceId && item.pageNumber === currentPageNumber)
        .some((item: any) => item.supplementRequired === true),
    )
    // Keep annotationCountsRef in sync so the auto-init effect can check counts without deps loop
    useEffect(() => {
      annotationCountsRef.current = annotationCounts
    }, [annotationCounts])

    // Auto-initialize chip counts without requiring a click:
    // - DB-reload: read from Redux extractedText (already populated from detectedValues)
    // - Fresh batch: auto-fetch EvaluatedText API (extractedText is [] from addMarkAnnotate)
    //
    // RETRY LOGIC: When the worker flips JobStatusId to Completed there is a tiny
    // window where the EvaluatedText GET can still return value:[] (RTK Query
    // cache from a prior attempt, or read-after-write visibility). Without a
    // retry the chip stays stuck at (0) even though DetectedValue has all the
    // records. Only mark an id as fetched once we get a non-empty response, and
    // schedule short retries (with backoff) for Completed items that return 0.
    const pendingRetriesRef = React.useRef<Record<string, { attempts: number; timer: number | null }>>({})

    useEffect(() => {
      const toInitFromRedux: Record<string, number> = {}
      const toFetch: any[] = []

      annotationListStatus.forEach((item: any) => {
        if (item.annotateStatus !== 'Completed') return
        const id = item.annotateList?.[0]?.evidenceAnnotationId
        if (!id) return
        // Already have a confirmed non-zero count → nothing to do.
        if (annotationCountsRef.current[id] > 0) return
        // Already fetched and got a confirmed empty (max retries reached) → skip.
        if (fetchedIdsRef.current.has(id)) return

        if (Array.isArray(item.extractedText) && item.extractedText.length > 0) {
          toInitFromRedux[id] = item.extractedText.length
          fetchedIdsRef.current.add(id)
        } else {
          toFetch.push(item)
        }
      })

      if (Object.keys(toInitFromRedux).length > 0) {
        setAnnotationCounts((prev) => ({ ...prev, ...toInitFromRedux }))
      }

      const MAX_RETRIES = 6 // ~0.5s + 1s + 2s + 3s + 5s + 8s ≈ 19.5s window
      const RETRY_DELAYS = [500, 1000, 2000, 3000, 5000, 8000]

      const fetchWithRetry = (item: any, ids: string[]) => {
        const attempt = (id: string) => {
          getExtractedText(id, false /* preferCacheValue=false → force network */).unwrap()
            .then((response: any) => {
              const count = Array.isArray(response?.value)
                ? countRecordsInValue(response.value, item.annotationTypeId)
                : 0

              if (count > 0) {
                fetchedIdsRef.current.add(id)
                delete pendingRetriesRef.current[id]
                setAnnotationCounts((prev) => ({ ...prev, [id]: count }))
                return
              }

              // Empty response — schedule retry with backoff.
              const state = pendingRetriesRef.current[id] ?? { attempts: 0, timer: null }
              if (state.attempts >= MAX_RETRIES) {
                // Genuinely empty — accept and stop retrying.
                fetchedIdsRef.current.add(id)
                delete pendingRetriesRef.current[id]
                setAnnotationCounts((prev) => ({ ...prev, [id]: 0 }))
                return
              }
              const delay = RETRY_DELAYS[state.attempts] ?? 8000
              state.attempts += 1
              state.timer = window.setTimeout(() => attempt(id), delay)
              pendingRetriesRef.current[id] = state
            })
            .catch(() => {
              // Network/server error — retry with backoff up to MAX_RETRIES.
              const state = pendingRetriesRef.current[id] ?? { attempts: 0, timer: null }
              if (state.attempts >= MAX_RETRIES) {
                fetchedIdsRef.current.add(id)
                delete pendingRetriesRef.current[id]
                return
              }
              const delay = RETRY_DELAYS[state.attempts] ?? 8000
              state.attempts += 1
              state.timer = window.setTimeout(() => attempt(id), delay)
              pendingRetriesRef.current[id] = state
            })
        }

        ids.forEach((id) => {
          // Reset retry state for this id (in case a previous batch left it).
          if (pendingRetriesRef.current[id]?.timer) {
            window.clearTimeout(pendingRetriesRef.current[id].timer as number)
          }
          pendingRetriesRef.current[id] = { attempts: 0, timer: null }
          attempt(id)
        })
      }

      toFetch.forEach((item) => {
        const ids: string[] = (item.annotateList as any[])
          .map((a: any) => a.evidenceAnnotationId)
          .filter(Boolean)
        if (ids.length === 0) return
        fetchWithRetry(item, ids)
      })
    }, [annotationListStatus])

    // Cleanup any in-flight retry timers on unmount.
    useEffect(() => {
      return () => {
        Object.values(pendingRetriesRef.current).forEach((state) => {
          if (state?.timer) window.clearTimeout(state.timer as number)
        })
        pendingRetriesRef.current = {}
      }
    }, [])

    useEffect(() => {
      if (shouldRunEffect) {
        if (annotationListStatus.length > 0 && currentPageNumber) {
          const lastItem = annotationListStatus[annotationListStatus.length - 1]
          // Only auto-select a chip that has finished processing; if the chip is still
          // not-started or InProgress the poller will flip it to Completed later and
          // this effect will re-fire at that point with the correct status.
          if (lastItem?.annotateStatus === 'Completed') {
            handleTextExtraction(lastItem)
          }
        }
      }
    }, [annotationListStatus, shouldRunEffect, currentPageNumber])

    useEffect(() => {
      if (toastMessage.isShow) {
        setTimeout(() => {
          setToastMessage({
            isShow: false,
            message: '',
            type: undefined,
          })
        }, 2000)
      }
    }, [toastMessage.isShow])

    useEffect(() => {
      const fetchAnnotationList = async () => {
        try {
          const response = await getAnnotationList(evidenceId).unwrap()
          if (response && response.length > 0) {
            const annotationList: {
              evidenceId: any
              annotationTypeId: any
              annotationTypeLabel: any
              annotateList: [
                {
                  annotateIndex: number
                  evidenceAnnotationId: any
                  jobId: string
                  topLeftX: any
                  topLeftY: any
                  bottomRightX: any
                  bottomRightY: any
                  status: string
                },
              ]
              annotateStatus: string
              isError: boolean
              errorText: string
              extractedText: any
              pageNumber: number // Add pageNumber to track the current page
            }[] = []
            response.forEach((annotation: any, index: number) => {
              const {
                id,
                evidenceId,
                annotationTypeId,
                annotationType,
                topLeftX,
                topLeftY,
                bottomRightX,
                bottomRightY,
                detectedValues,
                pageNumber,
                validated,
              } = annotation
              const annotateList: [
                {
                  annotateIndex: number
                  evidenceAnnotationId: any
                  jobId: string
                  topLeftX: any
                  topLeftY: any
                  bottomRightX: any
                  bottomRightY: any
                  status: string
                  pageNumber?: number // Add pageNumber if needed
                  validated?: boolean
                },
              ] = [
                  {
                    annotateIndex: index,
                    evidenceAnnotationId: id,
                    jobId: '',
                    topLeftX: topLeftX,
                    topLeftY: topLeftY,
                    bottomRightX: bottomRightX,
                    bottomRightY: bottomRightY,
                    status: detectedValues[0]?.id ? 'Completed' : 'not-started',
                    validated: typeof validated === 'boolean' ? validated : true,
                  },
                ]
              if (typeof (detectedValues[0]?.evaluatedValue) !== 'undefined') {
                annotationList.push({
                  evidenceId: evidenceId,
                  annotationTypeId: annotationTypeId,
                  annotationTypeLabel: annotationType,
                  annotateList: annotateList,
                  annotateStatus: detectedValues[0]?.id
                    ? 'Completed'
                    : 'Completed',
                  isError: false,
                  errorText: '',
                  extractedText: getExtractedData(
                    detectedValues[0]?.evaluatedValue,
                    annotationType,
                  ),
                  pageNumber: pageNumber, // Add pageNumber to the annotationList
                })
              }
            })
            if (import.meta.env.DEV) {
              const byType = annotationList.reduce((acc: Record<string, any[]>, item) => {
                const key = `${item.annotationTypeLabel} (p${item.pageNumber})`
                acc[key] = acc[key] ?? []
                acc[key].push(item.annotateList[0])
                return acc
              }, {})
              console.log('[SOD Annotation] getAnnotationList regions by type:', byType)
            }
            dispatch(addAnnotationList(annotationList))
          }
        } catch (error) {
          console.error('Error fetching annotation list:', error)
        }
      }

      // Prevent multiple calls by checking if annotationListStatus is already populated
      if (annotationListStatus.length === 0) {
        fetchAnnotationList()
      }
    }, [evidenceId])

    const handleTextExtraction = async (annotatedItem: any) => {
      onSelectedAnnotation(annotatedItem)
      setSelectedAnnotateType(annotatedItem.annotationTypeId)

      const response = await getAnnotationList(evidenceId).unwrap()
      const systemAccountDetectedData = response.map((annotation: any) => {
        return {
          evidenceId: annotation.evidenceId,
          detected: annotation.systemAccountDetected
        }
      })
      setSystemAccountDetected(systemAccountDetectedData)

      // Collect all persisted annotation IDs in this group (multi-region support)
      const ids: string[] = (annotatedItem.annotateList as any[])
        .map((a) => a.evidenceAnnotationId)
        .filter(Boolean)

      if (import.meta.env.DEV) {
        console.log(
          `[SOD Extraction] fetchingEvaluatedText for ${ids.length} region(s):`,
          ids,
          'type:', annotatedItem.annotationTypeLabel,
        )
      }

      if (ids.length === 0) return

      setSelectedAnnotate(ids[0])

      // If the user has manually edited this annotation, restore from Redux flat-row cache
      // instead of re-fetching from the API (preserves unsaved field edits across chip switches).
      // We reconstruct a single chunk from the flat extractedText rows (always current, unlike
      // rawChunks which only updates on add/delete row, not on individual field edits).
      if (annotatedItem.isDirty && Array.isArray(annotatedItem.extractedText) && annotatedItem.extractedText.length > 0) {
        const recordArrayKey = getRecordArrayKey(annotatedItem.annotationTypeId)
        const reconstructedChunk = recordArrayKey
          ? JSON.stringify({ [recordArrayKey]: annotatedItem.extractedText })
          : JSON.stringify(annotatedItem.extractedText)
        setExtractedText([reconstructedChunk])
        const rowCount = annotatedItem.extractedText.length
        const cachedCounts: Record<string, number> = {}
        ids.forEach((id) => { cachedCounts[id] = rowCount })
        setAnnotationCounts((prev) => ({ ...prev, ...cachedCounts }))
        //setIsSupplymentRequired(annotatedItem.supplementRequired ?? false)
        dispatch(setSupplementRequired({ value: annotatedItem.supplementRequired ?? false, evidenceId, pageNumber: currentPageNumber ?? 1 }))
        return
      }

      try {
        const responses = await Promise.allSettled(
          ids.map((id) => getExtractedText(id).unwrap()),
        )

        if (import.meta.env.DEV) {
          responses.forEach((r, i) => {
            if (r.status === 'fulfilled') {
              console.log(`[SOD Extraction] region[${i}] id=${ids[i]} →`, r.value)
            } else {
              console.warn(`[SOD Extraction] region[${i}] id=${ids[i]} failed:`, r.reason)
            }
          })
        }
        let supplementRequired = false
        const merged: any[] = []
        responses.forEach((r) => {
          if (r.status === 'fulfilled' && r.value) {
            if (r.value.supplementRequired) supplementRequired = true
            if (Array.isArray(r.value.value)) merged.push(...r.value.value)
          }
        })
        //setIsSupplymentRequired(supplementRequired)
        dispatch(setSupplementRequired({ value: supplementRequired, evidenceId, pageNumber: currentPageNumber ?? 1 }))
        if (merged.length === 0) {
          setToastMessage({
            isShow: true,
            message: 'Extracted Text is empty',
            type: 'warning',
          })
        } else {
          setExtractedText(merged)
          // Store fetched chunks as both rawChunks and originalChunks (baseline for diff highlight)
          dispatch(storeChunksForAnnotation({ evidenceAnnotationId: ids[0], chunks: merged, storeOriginal: true }))
          // Update per-ID counts in local state (avoids Redux cross-contamination
          // where two chips sharing the same annotationTypeId overwrite each other).
          const newCounts: Record<string, number> = {}
          ids.forEach((id, i) => {
            const r = responses[i]
            if (r?.status === 'fulfilled' && Array.isArray(r.value?.value)) {
              newCounts[id] = countRecordsInValue(r.value.value, annotatedItem.annotationTypeId)
            }
          })
          setAnnotationCounts((prev) => ({ ...prev, ...newCounts }))
        }
      } catch (error) {
        setToastMessage({
          isShow: true,
          message: 'Text Extraction has failed.',
          type: 'error',
        })
        console.error('[SOD Extraction] error:', error)
      }
      // Logic to handle text extraction
    }

    const openDeleteAnnotationModal = (item: any) => {
      setDeleteAnnotationModal({ show: true, item })
    }

    const closeDeleteAnnotationModal = () => {
      setDeleteAnnotationModal({ show: false, item: null })
    }

    const handleDelete = async (selectedItem: any) => {
      const newLocal = selectedItem.annotateList[0]
      const { evidenceAnnotationId, jobId } = newLocal

      if (selectedItem.extractedText && selectedItem.extractedText.length === 0) {
        setToastMessage({
          isShow: true,
          message: 'Annotation deleted successfully',
          type: 'success',
        })
        setExtractedText([])
        dispatch(
          removeAnnotationByJobId({
            evidenceId: evidenceId,
            evidenceAnnotationId: evidenceAnnotationId,
          }),
        )
        onDeleteAnnotation(evidenceAnnotationId)
        return
      }
      try {
        const deleteResponse =
          await deleteAnnotation(evidenceAnnotationId).unwrap()
        if (deleteResponse) {
          setToastMessage({
            isShow: true,
            message: 'Annotation deleted successfully',
            type: 'success',
          })
          setExtractedText([])
          dispatch(
            removeAnnotationByJobId({
              evidenceId: evidenceId,
              evidenceAnnotationId: evidenceAnnotationId,
            }),
          )
          onDeleteAnnotation(evidenceAnnotationId)
        }
      } catch (error) {
        console.error('Error deleting extracted text:', error)
      }
    }

    const handleClose = () => {
      setToastMessage({
        isShow: false,
        message: '',
        type: undefined,
      })
    }

    const handleConfirmNoSystemAccount = async () => {
      if (!selectedAnnotate) return
      setShowConfirmModal(false)
      try {
        await confirmNoSystemAccount(selectedAnnotate).unwrap()
        //setIsSupplymentRequired(false)
        dispatch(setSupplementRequired({ value: false, evidenceId, pageNumber: currentPageNumber ?? 1 }))
        setToastMessage({
          isShow: true,
          message: 'Confirmed — no system account. Annotation cleared.',
          type: 'success',
        })
        onSystemAccountAcknowledged?.()
      } catch (error) {
        setToastMessage({
          isShow: true,
          message: 'Failed to confirm. Please try again.',
          type: 'error',
        })
        console.error('Error acknowledging system account:', error)
      }
    }

    const PLACEHOLDER_LIMIT = 5

    const isUserDetailsType = (annotationTypeId: string) =>
      annotationTypeId === ConstAnnotationTypeId.DevUserDetails ||
      annotationTypeId === ConstAnnotationTypeId.ImplUserDetails

    const countRecordsInValue = (valueChunks: string[], annotationTypeId: string): number => {
      const key = getRecordArrayKey(annotationTypeId)
      if (!key) return 0
      return valueChunks.reduce((sum, chunk) => {
        try {
          const parsed = typeof chunk === 'string' ? JSON.parse(chunk) : chunk
          return sum + (Array.isArray(parsed?.[key]) ? parsed[key].length : 0)
        } catch {
          return sum
        }
      }, 0)
    }

    const getRecordArrayKey = (annotationTypeId: string) => {
      const id = annotationTypeId?.toUpperCase()
      if (id === ConstAnnotationTypeId.DevUserDetails.toUpperCase() || id === ConstAnnotationTypeId.ImplUserDetails.toUpperCase()) {
        return 'userDetails'
      }
      if (id === ConstAnnotationTypeId.DevUserGroup.toUpperCase() || id === ConstAnnotationTypeId.ImplUserGroup.toUpperCase()) {
        return 'userGroup'
      }
      if (id === ConstAnnotationTypeId.DevPermissions.toUpperCase() || id === ConstAnnotationTypeId.ImplPermissions.toUpperCase()) {
        // BE serializes LLMPermissionListJSONDTO.Permission (singular) as camelCase "permission".
        return 'permission'
      }
      if (id === ConstAnnotationTypeId.DevOther.toUpperCase() || id === ConstAnnotationTypeId.ImplOther.toUpperCase()) {
        return 'other'
      }
      if (id === ConstAnnotationTypeId.DevDateAndTime.toUpperCase() || id === ConstAnnotationTypeId.ImplDateAndTime.toUpperCase()) {
        return 'dateAndTime'
      }
      return ''
    }

    const getEmptyRecordForType = (annotationTypeId: string) => {
      if (annotationTypeId === ConstAnnotationTypeId.DevUserDetails || annotationTypeId === ConstAnnotationTypeId.ImplUserDetails) {
        return {
          personName: '',
          personEmail: '',
          personId: '',
          userGroup: '',
          personJob: '',
          personDepartment: '',
          permission: '',
          systemAccount: '',
          sysAccountDetected: 0,
        }
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevUserGroup || annotationTypeId === ConstAnnotationTypeId.ImplUserGroup) {
        return {
          personName: '',
          userGroup: '',
          permission: '',
        }
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevPermissions || annotationTypeId === ConstAnnotationTypeId.ImplPermissions) {
        return {
          personName: '',
          userGroup: '',
          permission: '',
        }
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevOther || annotationTypeId === ConstAnnotationTypeId.ImplOther) {
        return {
          personName: '',
          userGroup: '',
          list: '',
        }
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevDateAndTime || annotationTypeId === ConstAnnotationTypeId.ImplDateAndTime) {
        return {
          dateAndTime: [''],
        }
      }
      return {}
    }

    const formatRecordFieldLabel = (key: string) =>
      key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/^./, (char) => char.toUpperCase())

    const formatRecordFieldValue = (value: any) => {
      if (value === null || value === undefined || value === '') return '-'
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    }

    const deleteAnnotationPreviewColumns = useMemo(
      () => [
        { headerName: '#', field: 'index', width: 60, sortable: false, filter: false, suppressMenu: true },
        { headerName: 'Preview', field: 'preview', flex: 1, sortable: false, filter: false, suppressMenu: true },
      ],
      [],
    )

    const deleteAnnotationPreviewRows = useMemo(() => {
      const rows: any[] = deleteAnnotationModal.item?.extractedText ?? []
      if (rows.length === 0) return [{ index: '-', preview: '(no records)' }]
      return rows.map((row: any, i: number) => {
        const firstEntry = Object.entries(row).find(
          ([k, v]) =>
            k !== 'sysAccountDetected' &&
            v !== null &&
            v !== undefined &&
            v !== '' &&
            v !== 0 &&
            !Array.isArray(v),
        )
        return {
          index: `#${i + 1}`,
          preview: firstEntry
            ? `${formatRecordFieldLabel(String(firstEntry[0]))}: ${String(firstEntry[1])}`
            : '(empty)',
        }
      })
    }, [deleteAnnotationModal.item])

    const deletePreviewColumns = useMemo(
      () => [
        {
          headerName: 'Field',
          field: 'field',
          flex: 1,
          sortable: false,
          filter: false,
          suppressMenu: true,
        },
        {
          headerName: 'Value',
          field: 'value',
          flex: 2,
          sortable: false,
          filter: false,
          suppressMenu: true,
        },
      ],
      [],
    )

    const deletePreviewRows = useMemo(() => {
      if (!deleteRecordModal.record || Object.keys(deleteRecordModal.record).length === 0) {
        return [{ field: 'Record', value: '-' }]
      }

      return Object.entries(deleteRecordModal.record).map(([key, value]) => ({
        field: formatRecordFieldLabel(key),
        value: formatRecordFieldValue(value),
      }))
    }, [deleteRecordModal.record])

    const getRecordFromChunk = (chunkIndex: number, recordIndex: number, annotationTypeId: string) => {
      const recordArrayKey = getRecordArrayKey(annotationTypeId)
      if (!recordArrayKey) return null

      const chunk = extractedText[chunkIndex]
      const parsed = chunk ? JSON.parse(chunk) : {}
      const rows = Array.isArray(parsed[recordArrayKey]) ? parsed[recordArrayKey] : []

      return rows[recordIndex] ?? null
    }

    const getFlattenedRowsFromChunks = (chunks: any[], annotationTypeId: string) => {
      const recordArrayKey = getRecordArrayKey(annotationTypeId)
      if (!recordArrayKey) return []

      return chunks.flatMap((chunk: any) => {
        try {
          const parsed = chunk ? JSON.parse(chunk) : {}
          return Array.isArray(parsed[recordArrayKey]) ? parsed[recordArrayKey] : []
        } catch {
          return []
        }
      })
    }

    const updateChunkAndSyncRows = (
      chunkIndex: number,
      annotationTypeId: string,
      updateRows: (rows: any[]) => any[],
    ) => {
      const recordArrayKey = getRecordArrayKey(annotationTypeId)
      if (!recordArrayKey) return

      const updatedChunks = [...extractedText]
      const chunk = updatedChunks[chunkIndex]
      const parsed = chunk ? JSON.parse(chunk) : {}
      const currentRows = Array.isArray(parsed[recordArrayKey]) ? parsed[recordArrayKey] : []
      parsed[recordArrayKey] = updateRows(currentRows)
      updatedChunks[chunkIndex] = JSON.stringify(parsed) as any

      setExtractedText(updatedChunks)

      dispatch(
        setAnnotationExtractedText({
          evidenceId,
          annotationTypeId: selectedAnnotateType,
          evidenceAnnotationId: selectedAnnotate,
          extractedText: getFlattenedRowsFromChunks(updatedChunks, annotationTypeId),
          pageNumber: currentPageNumber ?? 1,
        }),
      )
    }

    const emptyRowIndicesFromRedux = useMemo(() => {
      if (!showPlaceholderLimitError) return new Set<number>()
      const currentAnnotation = annotationListStatus.find(
        (item: any) => item.annotationTypeId === selectedAnnotateType,
      )
      const rows: any[] = currentAnnotation?.extractedText ?? []
      const indices = new Set<number>()
      rows.forEach((row: any, i: number) => {
        if (!row?.personName && !row?.personEmail && !row?.personId) {
          indices.add(i)
        }
      })
      return indices
    }, [annotationListStatus, showPlaceholderLimitError, selectedAnnotateType])

    useEffect(() => {
      if (!showPlaceholderLimitError) return
      const currentAnnotation = annotationListStatus.find(
        (item: any) => item.annotationTypeId === selectedAnnotateType,
      )
      const emptyCount = (currentAnnotation?.extractedText ?? []).filter(
        (row: any) => !row?.personName && !row?.personEmail && !row?.personId,
      ).length
      if (emptyCount < PLACEHOLDER_LIMIT) {
        setShowPlaceholderLimitError(false)
      }
    }, [annotationListStatus, showPlaceholderLimitError, selectedAnnotateType])

    const handleAddRecord = (chunkIndex: number, annotationTypeId: string) => {
      const recordArrayKey = getRecordArrayKey(annotationTypeId)
      if (!recordArrayKey) return

      const currentAnnotation = annotationListStatus.find(
        (item: any) => item.annotationTypeId === selectedAnnotateType,
      )
      const rows: any[] = currentAnnotation?.extractedText ?? []

      if (isUserDetailsType(annotationTypeId)) {
        const emptyCount = rows.filter(
          (row: any) => !row?.personName && !row?.personEmail && !row?.personId,
        ).length
        if (emptyCount >= PLACEHOLDER_LIMIT) {
          setShowPlaceholderLimitError(true)
          return
        }
      }

      setShowPlaceholderLimitError(false)
      const emptyRow = getEmptyRecordForType(annotationTypeId)

      updateChunkAndSyncRows(chunkIndex, annotationTypeId, (currentRows) => [
        ...currentRows,
        emptyRow,
      ])
    }

    const handleDeleteRecord = (
      chunkIndex: number,
      recordIndex: number,
      annotationTypeId: string,
    ) => {
      const recordArrayKey = getRecordArrayKey(annotationTypeId)
      if (!recordArrayKey) return

      updateChunkAndSyncRows(chunkIndex, annotationTypeId, (rows) =>
        rows.filter((_: any, idx: number) => idx !== recordIndex),
      )
    }

    const handleDeleteButtonClick = (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget as HTMLElement
      const chunkIndex = Number(target.dataset.chunkIndex)
      const recordIndex = Number(target.dataset.recordIndex)
      const annotationTypeId = target.dataset.annotationTypeId ?? ''

      if (Number.isNaN(chunkIndex) || Number.isNaN(recordIndex) || !annotationTypeId) {
        return
      }

      setDeleteRecordModal({
        show: true,
        chunkIndex,
        recordIndex,
        annotationTypeId,
        record: getRecordFromChunk(chunkIndex, recordIndex, annotationTypeId),
      })
    }

    const closeDeleteRecordModal = () => {
      setDeleteRecordModal({
        show: false,
        chunkIndex: -1,
        recordIndex: -1,
        annotationTypeId: '',
        record: null,
      })
    }

    const handleConfirmDeleteRecord = () => {
      const { chunkIndex, recordIndex, annotationTypeId } = deleteRecordModal
      if (chunkIndex < 0 || recordIndex < 0 || !annotationTypeId) {
        closeDeleteRecordModal()
        return
      }

      handleDeleteRecord(chunkIndex, recordIndex, annotationTypeId)
      closeDeleteRecordModal()
    }

    const getListItem = (
      userDetails: {
        userDetails: any
        userGroup: any
        permissions: any
        permission: any
        other: any
        dateAndTime: any
      },
      index: number,
      annotationId: string,
    ) => {
      // Derive original (pre-edit) data for the same chunk to drive field-level diff highlight
      const activeEntry = annotationListStatus.find(
        (item: any) => item.annotateList?.some((a: any) => a.evidenceAnnotationId === annotationId),
      )
      const originalParsed: any = activeEntry?.originalChunks?.[index]
        ? (() => { try { return JSON.parse(activeEntry.originalChunks[index]) } catch { return null } })()
        : null
      const { userGroup, dateAndTime } = userDetails
      const annotationTypeId = selectedAnnotateType.toUpperCase()
      const systemAccountCount = systemAccountDetected?.find((item: any) => item.evidenceId === evidenceId)?.detected ?? 0
      if (annotationTypeId === ConstAnnotationTypeId.DevUserDetails || annotationTypeId === ConstAnnotationTypeId.ImplUserDetails) {
        return (
          <>
            {systemAccountCount ? (
              <div className="system-account-warning-container">
                <div className="info-icon">
                  <MotifIcon src={IconoirInfoCircle} size="18" fill="#1289ad" />
                </div>
                <div className="warning-text">
                  {systemAccountCount} system {systemAccountCount > 1 ? 'accounts' : 'account'} has been identified.
                </div>
              </div>
            ) : null}
            {userDetails?.userDetails &&
              userDetails?.userDetails?.map((user: any, itemIndex: number) => (
                <div
                  key={`${itemIndex}_${currentPageNumber}_${selectedAnnotateType}`}
                  className={`user-detail-record${showPlaceholderLimitError && emptyRowIndicesFromRedux.has(itemIndex) ? ' user-detail-record--highlight' : ''}`}
                >
                  <div className="record-header">
                    <span className="record-title">Record #{itemIndex + 1}</span>
                    <MotifIconButton
                      className="delete-record-btn"
                      onClick={handleDeleteButtonClick}
                      data-chunk-index={index}
                      data-record-index={itemIndex}
                      data-annotation-type-id={annotationTypeId}
                      aria-label="Delete record"
                      title="Delete record"
                    >
                      <MotifIcon src={MotifActionIcDelete24px} />
                    </MotifIconButton>
                  </div>
                  <UserDetail
                    evidenceId={evidenceId}
                    recordIndex={itemIndex}
                    annotationTypeId={selectedAnnotateType}
                    pageNumber={currentPageNumber ?? 1}
                    {...user}
                    evidenceAnnotationId={annotationId}
                    originalRecord={originalParsed?.userDetails?.[itemIndex]}
                  />
                </div>
              ))}
            <div className="add-row-controls">
              <MotifButton
                className="add-placeholder-row-btn"
                onClick={() => handleAddRecord(index, annotationTypeId)}
                size="small"
                type="button"
                variant="secondary"
              >
                + Add record
              </MotifButton>
              {showPlaceholderLimitError && (
                <p className="placeholder-limit-message">
                  You may only add five placeholders at a time. Please update the annotations and then try again.
                </p>
              )}
            </div>
          </>
        )
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevUserGroup || annotationTypeId === ConstAnnotationTypeId.ImplUserGroup) {
        return (
          <>
            {userGroup &&
              userGroup.map((userGroup: any, itemIndex: number) => (
                <div
                  key={`${itemIndex}_${currentPageNumber}_${selectedAnnotateType}`}
                  className="user-detail-record"
                >
                  <div className="record-header">
                    <span className="record-title">Record #{itemIndex + 1}</span>
                    <MotifIconButton
                      className="delete-record-btn"
                      onClick={handleDeleteButtonClick}
                      data-chunk-index={index}
                      data-record-index={itemIndex}
                      data-annotation-type-id={annotationTypeId}
                      aria-label="Delete record"
                      title="Delete record"
                    >
                      <MotifIcon src={MotifActionIcDelete24px} />
                    </MotifIconButton>
                  </div>
                  <UserGroup
                    evidenceId={evidenceId}
                    recordIndex={itemIndex}
                    annotationTypeId={selectedAnnotateType}
                    pageNumber={currentPageNumber}
                    {...userGroup}
                    evidenceAnnotationId={annotationId}
                    originalRecord={originalParsed?.userGroup?.[itemIndex]}
                  />
                </div>
              ))}
            <div className="add-row-controls">
              <MotifButton
                className="add-placeholder-row-btn"
                onClick={() => handleAddRecord(index, annotationTypeId)}
                size="small"
                type="button"
                variant="secondary"
              >
                + Add record
              </MotifButton>
            </div>
          </>
        )
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevPermissions || annotationTypeId === ConstAnnotationTypeId.ImplPermissions) {
        const permissionRows = userDetails?.permissions ?? userDetails?.permission
        return (
          <>
            {permissionRows &&
              permissionRows?.map(
                (permission: any, itemIndex: number) => (
                  <div
                    key={`${itemIndex}_${currentPageNumber}_${selectedAnnotateType}`}
                    className="user-detail-record"
                  >
                    <div className="record-header">
                      <span className="record-title">Record #{itemIndex + 1}</span>
                      <MotifIconButton
                        className="delete-record-btn"
                        onClick={handleDeleteButtonClick}
                        data-chunk-index={index}
                        data-record-index={itemIndex}
                        data-annotation-type-id={annotationTypeId}
                        aria-label="Delete record"
                        title="Delete record"
                      >
                        <MotifIcon src={MotifActionIcDelete24px} />
                      </MotifIconButton>
                    </div>
                    <UserPermission
                      evidenceId={evidenceId}
                      recordIndex={itemIndex}
                      annotationTypeId={selectedAnnotateType}
                      pageNumber={currentPageNumber}
                      {...permission}
                      evidenceAnnotationId={annotationId}
                      originalRecord={(originalParsed?.permissions ?? originalParsed?.permission)?.[itemIndex]}
                    />
                  </div>
                ),
              )}
            <div className="add-row-controls">
              <MotifButton
                className="add-placeholder-row-btn"
                onClick={() => handleAddRecord(index, annotationTypeId)}
                size="small"
                type="button"
                variant="secondary"
              >
                + Add record
              </MotifButton>
            </div>
          </>
        )
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevOther || annotationTypeId === ConstAnnotationTypeId.ImplOther) {
        return (
          <>
            {userDetails?.other &&
              userDetails?.other?.map((other: any, itemIndex: number) => (
                <div
                  key={`${itemIndex}_${currentPageNumber}_${selectedAnnotateType}`}
                  className="user-detail-record"
                >
                  <div className="record-header">
                    <span className="record-title">Record #{itemIndex + 1}</span>
                    <MotifIconButton
                      className="delete-record-btn"
                      onClick={handleDeleteButtonClick}
                      data-chunk-index={index}
                      data-record-index={itemIndex}
                      data-annotation-type-id={annotationTypeId}
                      aria-label="Delete record"
                      title="Delete record"
                    >
                      <MotifIcon src={MotifActionIcDelete24px} />
                    </MotifIconButton>
                  </div>
                  <OtherDetails
                    evidenceId={evidenceId}
                    recordIndex={itemIndex}
                    annotationTypeId={selectedAnnotateType}
                    pageNumber={currentPageNumber}
                    {...other}
                    evidenceAnnotationId={annotationId}
                    originalRecord={originalParsed?.other?.[itemIndex]}
                  />
                </div>
              ))}
            <div className="add-row-controls">
              <MotifButton
                className="add-placeholder-row-btn"
                onClick={() => handleAddRecord(index, annotationTypeId)}
                size="small"
                type="button"
                variant="secondary"
              >
                + Add record
              </MotifButton>
            </div>
          </>
        )
      }
      if (annotationTypeId === ConstAnnotationTypeId.DevDateAndTime || annotationTypeId === ConstAnnotationTypeId.ImplDateAndTime) {
        return (
          <>
            {userDetails?.dateAndTime &&
              userDetails?.dateAndTime?.map((other: any, itemIndex: number) => (
                <div
                  key={`${itemIndex}_${currentPageNumber}_${selectedAnnotateType}`}
                  className="user-detail-record"
                >
                  <div className="record-header">
                    <span className="record-title">Record #{itemIndex + 1}</span>
                    <MotifIconButton
                      className="delete-record-btn"
                      onClick={handleDeleteButtonClick}
                      data-chunk-index={index}
                      data-record-index={itemIndex}
                      data-annotation-type-id={annotationTypeId}
                      aria-label="Delete record"
                      title="Delete record"
                    >
                      <MotifIcon src={MotifActionIcDelete24px} />
                    </MotifIconButton>
                  </div>
                  <DateTime
                    evidenceId={evidenceId}
                    recordIndex={itemIndex}
                    annotationTypeId={selectedAnnotateType}
                    dateAndTime={other?.dateAndTime ?? []}
                    pageNumber={currentPageNumber ?? 1}
                    evidenceAnnotationId={annotationId}
                    originalRecord={originalParsed?.date?.[itemIndex] ?? originalParsed?.dateAndTime?.[itemIndex]}
                  />
                </div>
              ))}
            <div className="add-row-controls">
              <MotifButton
                className="add-placeholder-row-btn"
                onClick={() => handleAddRecord(index, annotationTypeId)}
                size="small"
                type="button"
                variant="secondary"
              >
                + Add record
              </MotifButton>
            </div>
          </>
        )
      }
    }
    // see how to determine nothing is visible
    return (
      <div className="extracted-text-tab">
        {/* Fixed-position toast so it's always visible regardless of scroll position */}
        {toastMessage.isShow && (
          <div className="extracted-text-toast-fixed">
            <MotifToast
              variant={toastMessage.type}
              onClose={() => handleClose()}
            >
              {toastMessage.message}
            </MotifToast>
          </div>
        )}

        {/* Confirm modal — prevents accidental clicks */}
        <MotifModal
          show={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          size="sm"
        >
          <MotifModalHeader>Confirm No System Account</MotifModalHeader>
          <MotifModalBody>
            Are you sure there is no system/service account in this annotation? This will clear the system account flag and cannot be undone.
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </MotifButton>
            <MotifButton variant="primary" onClick={handleConfirmNoSystemAccount}>
              Yes, Confirm
            </MotifButton>
          </MotifModalFooter>
        </MotifModal>

        <MotifModal
          show={deleteRecordModal.show}
          onClose={closeDeleteRecordModal}
          size="md"
        >
          <MotifModalHeader>Confirm Delete Record</MotifModalHeader>
          <MotifModalBody>
            <p className="delete-record-message">Are you sure to delete the following record:</p>
            <div className="delete-record-preview-grid">
              <Table
                columnDefs={deletePreviewColumns}
                cellRenderers={deletePreviewRows}
                TitleComplement={EmptyTitleComplement}
                title="Record Preview"
                description="Review the extracted fields before confirming delete"
                pagination={false}
                rowHeight={40}
              />
            </div>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton variant="secondary" onClick={closeDeleteRecordModal}>
              Cancel
            </MotifButton>
            <MotifButton variant="primary" onClick={handleConfirmDeleteRecord}>
              Confirm
            </MotifButton>
          </MotifModalFooter>
        </MotifModal>

        {/* Chip-level annotation delete confirmation */}
        <MotifModal
          show={deleteAnnotationModal.show}
          onClose={closeDeleteAnnotationModal}
          size="md"
        >
          <MotifModalHeader>Confirm Delete Annotation</MotifModalHeader>
          <MotifModalBody>
            <p className="delete-record-message">
              Are you sure you want to delete this region? It contains{' '}
              <strong>{deleteAnnotationModal.item?.extractedText?.length ?? 0} record(s)</strong>.
            </p>
            <div className="delete-record-preview-grid">
              <Table
                columnDefs={deleteAnnotationPreviewColumns}
                cellRenderers={deleteAnnotationPreviewRows}
                TitleComplement={EmptyTitleComplement}
                title="Records to delete"
                description="First non-empty field per record"
                pagination={false}
                rowHeight={40}
              />
            </div>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton variant="secondary" onClick={closeDeleteAnnotationModal}>
              Cancel
            </MotifButton>
            <MotifButton
              variant="primary"
              onClick={async () => {
                const item = deleteAnnotationModal.item
                closeDeleteAnnotationModal()
                await handleDelete(item)
              }}
            >
              Confirm
            </MotifButton>
          </MotifModalFooter>
        </MotifModal>

        <MotifChipGroup>
          {annotationListStatus.length > 0 &&
            annotationListStatus.map((item: any, index: number) => {
              const newLocal = item?.annotateList[0]
              const { evidenceAnnotationId } = newLocal
              // Use locally-tracked count (updated after EvaluatedText fetch) so
              // each chip shows its own region's record count, keyed by its own
              // evidenceAnnotationId. Falls back to Redux value before first click.
              const recordCount = annotationCounts[evidenceAnnotationId] ?? (Array.isArray(item?.extractedText) ? item.extractedText.length : 0)
              if (item.annotateStatus === 'not-started' || item.annotateStatus === 'InProgress') {
                return (
                  <MotifChip
                    key={`${index}_${item.annotationTypeId}_processing`}
                    variant="monochrome-light"
                    type="outlined"
                    disabled
                    title={`${item.annotationTypeLabel} — processing`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="chip-spinner" />
                      <span>{item.annotationTypeLabel}</span>
                    </div>
                  </MotifChip>
                )
              }
              if (item.annotateStatus === 'Completed') {
                // Not-yet-verified annotations get the same warning treatment
                // as the evidence-level "Yet To Be Verified" chip, so it's
                // obvious at a glance which annotation type still needs review.
                const isUnverified = newLocal.validated === false
                return (
                  <MotifChip
                    key={`${index}_${item.annotationTypeId}`}
                    variant={
                      isUnverified
                        ? 'warning'
                        : evidenceAnnotationId === selectedAnnotate
                          ? 'success'
                          : 'monochrome-light'
                    }
                    type="outlined"
                    onClick={() => {
                      setShouldRunEffect(false)
                      handleTextExtraction(item)
                    }}
                    title={`${item.annotationTypeLabel} (${recordCount})${isUnverified ? ' — not yet verified' : ''}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{`${item.annotationTypeLabel} (${recordCount})`}</span>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteAnnotationModal(item)
                        }}
                        style={{
                          display: 'flex',
                          cursor: 'pointer',
                        }}
                        title="Delete Annotation"
                      >
                        <MotifIcon src={MotifActionIcDelete24px} />
                      </div>
                    </div>
                  </MotifChip>
                )
              }
            })}
        </MotifChipGroup>
        <div className="extracted-text-content">
          <div>
            {extractedText.length > 0 &&
              extractedText.map((item, index) => {
                const parsedText = JSON.parse(item)
                return (
                  <React.Fragment key={index}>
                    {getListItem(parsedText, index, selectedAnnotate)}
                  </React.Fragment>
                )
              })}
          </div>
        </div>
      </div>
    )
  },
)
ExtractedTextItem.displayName = 'ExtractedTextItem'

export default ExtractedTextItem
